import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { trainingSessionAPI } from '../../services/api';
import { AttendanceStatus } from '../../types/event';
import type { EventPresence } from '../../types/trainingSession';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';

interface PresenceSectionProps {
  eventId: number;
  startTime: string;
}

type Draft = Record<number, boolean | null>;

const responseLabel = (status?: AttendanceStatus | null) => {
  switch (status) {
    case AttendanceStatus.Attending:
      return { text: 'Jön', className: 'bg-green-100 text-green-800' };
    case AttendanceStatus.NotAttending:
      return { text: 'Nem jön', className: 'bg-red-100 text-red-800' };
    case AttendanceStatus.Maybe:
      return { text: 'Talán', className: 'bg-yellow-100 text-yellow-800' };
    default:
      return { text: 'Nem válaszolt', className: 'bg-gray-100 text-gray-600' };
  }
};

/** A visszajelzésből javasolt megjelenés: aki jön, jelen; aki nem jön, hiányzik; a többi nyitott. */
const suggestFromResponse = (status?: AttendanceStatus | null): boolean | null => {
  if (status === AttendanceStatus.Attending) return true;
  if (status === AttendanceStatus.NotAttending) return false;
  return null;
};

/**
 * Ki jelent meg ténylegesen az edzésen. A visszajelzések alapján előtölti az ívet,
 * az edző pedig játékosonként felülírhatja. Csak a stáb látja; ha a szerver
 * elutasítja (nem az ő korosztálya), a szekció nem jelenik meg.
 */
const PresenceSection: React.FC<PresenceSectionProps> = ({ eventId, startTime }) => {
  const { showToast } = useToast();
  const [presence, setPresence] = useState<EventPresence | null>(null);
  const [hidden, setHidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setPresence(await trainingSessionAPI.getPresence(eventId));
    } catch (err: any) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        setHidden(true);
      } else {
        console.error('A jelenléti ív betöltése nem sikerült:', err);
      }
    }
  }, [eventId]);

  useEffect(() => {
    setEditing(false);
    setHidden(false);
    load();
  }, [load]);

  if (hidden || !presence) return null;

  // Az edzés napjától lehet rögzíteni - előre nincs értelme, ki volt ott
  const eventDay = new Date(startTime);
  eventDay.setHours(0, 0, 0, 0);
  const canRecord = eventDay.getTime() <= Date.now();

  const startEditing = () => {
    const initial: Draft = {};
    presence.players.forEach((p) => {
      initial[p.playerId] = presence.isRecorded
        ? p.isPresent ?? null
        : suggestFromResponse(p.response);
    });
    setDraft(initial);
    setEditing(true);
  };

  const setAll = (value: boolean) => {
    setDraft(Object.fromEntries(presence.players.map((p) => [p.playerId, value])));
  };

  const applyResponses = () => {
    setDraft((prev) => {
      const next = { ...prev };
      presence.players.forEach((p) => {
        const suggestion = suggestFromResponse(p.response);
        if (suggestion !== null) next[p.playerId] = suggestion;
      });
      return next;
    });
  };

  const toggle = (playerId: number, value: boolean) => {
    // Ugyanarra kattintva visszaáll "nincs rögzítve" állapotba
    setDraft((prev) => ({ ...prev, [playerId]: prev[playerId] === value ? null : value }));
  };

  const undecided = presence.players.filter((p) => draft[p.playerId] == null).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = Object.entries(draft)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([playerId, value]) => ({ playerId: Number(playerId), isPresent: value as boolean }));
      const saved = await trainingSessionAPI.savePresence(eventId, items);
      setPresence(saved);
      setEditing(false);
      showToast(`Jelenlét elmentve: ${saved.presentCount} jelen, ${saved.absentCount} hiányzott.`);
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A jelenlét mentése nem sikerült.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const notRecordedCount = presence.players.filter((p) => p.isPresent == null).length;

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ClipboardDocumentCheckIcon className="h-5 w-5 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">Jelenlét</h4>
        </div>
        {!editing && canRecord && presence.players.length > 0 && (
          <button
            type="button"
            onClick={startEditing}
            className="px-3 py-1.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium"
          >
            {presence.isRecorded ? 'Jelenlét szerkesztése' : 'Jelenlét rögzítése'}
          </button>
        )}
      </div>

      {!editing && (
        <>
          {presence.players.length === 0 ? (
            <p className="text-sm text-gray-500">Az eseményhez nem tartozik játékos.</p>
          ) : !presence.isRecorded ? (
            <p className="text-sm text-gray-500">
              {canRecord
                ? 'Még nincs rögzítve, ki jelent meg. A visszajelzések alapján előtöltjük az ívet.'
                : 'A jelenlétet az edzés napjától lehet rögzíteni.'}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <span className="text-green-600 font-medium">{presence.presentCount} jelen</span>
                <span className="text-red-600 font-medium">{presence.absentCount} hiányzott</span>
                {notRecordedCount > 0 && (
                  <span className="text-gray-500">{notRecordedCount} nincs rögzítve</span>
                )}
              </div>
              {presence.recordedByName && presence.recordedAt && (
                <p className="mt-1 text-xs text-gray-400">
                  Utoljára rögzítette: {presence.recordedByName},{' '}
                  {new Date(presence.recordedAt).toLocaleString('hu-HU', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </>
          )}
        </>
      )}

      {editing && (
        <>
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              onClick={applyResponses}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100"
            >
              Visszajelzések alapján
            </button>
            <button
              type="button"
              onClick={() => setAll(true)}
              className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100"
            >
              Mindenki jelen
            </button>
          </div>

          <ul className="divide-y divide-gray-200 bg-white rounded-md border border-gray-200 max-h-80 overflow-y-auto">
            {presence.players.map((player) => {
              const label = responseLabel(player.response);
              const value = draft[player.playerId];
              return (
                <li key={player.playerId} className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{player.name}</div>
                    <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium ${label.className}`}>
                      {label.text}
                    </span>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => toggle(player.playerId, true)}
                      aria-pressed={value === true}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                        value === true
                          ? 'bg-green-600 border-green-600 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-green-50'
                      }`}
                    >
                      Jelen
                    </button>
                    <button
                      type="button"
                      onClick={() => toggle(player.playerId, false)}
                      aria-pressed={value === false}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                        value === false
                          ? 'bg-red-600 border-red-600 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-red-50'
                      }`}
                    >
                      Hiányzott
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">
              {undecided > 0
                ? `${undecided} játékosnál nincs megjelölve semmi - náluk nem rögzítünk jelenlétet.`
                : 'Minden játékos meg van jelölve.'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                disabled={saving}
                className="flex-1 sm:flex-none px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 sm:flex-none px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? 'Mentés...' : 'Mentés'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PresenceSection;
